import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token && config.url?.includes(BACKEND_URL)) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('access_token');
      if (token) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        const isLoginPage = window.location.pathname.startsWith('/admin/login');
        if (!isLoginPage) window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

const toSnakeParams = (filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      const apiKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
      params.append(apiKey, value);
    }
  });
  return params;
};

const buildQuery = (params = {}) => {
  const query = toSnakeParams(params).toString();
  return query ? `?${query}` : '';
};

const asArray = (data) => (Array.isArray(data) ? data : []);
const asObject = (data) =>
  data !== null && typeof data === 'object' && !Array.isArray(data) ? data : {};

const handleError = (error, context) => {
  console.error(`Error ${context}:`, error);
  throw error;
};

export const api = {
  // Health
  health: async () => {
    try {
      const response = await axios.get(`${API_BASE}/health`);
      return asObject(response.data);
    } catch (error) { return handleError(error, 'health check'); }
  },

  // Properties
  properties: {
    getAll: async (filters = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/properties?${toSnakeParams(filters).toString()}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching properties'); }
    },
    getById: async (id) => {
      try {
        const response = await axios.get(`${API_BASE}/properties/${id}`);
        return asObject(response.data);
      } catch (error) { return handleError(error, `fetching property ${id}`); }
    },
    getLocations: async () => {
      try {
        const response = await axios.get(`${API_BASE}/properties/locations/list`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching locations'); }
    },
    getTypes: async () => {
      try {
        const response = await axios.get(`${API_BASE}/properties/types/list`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching property types'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/admin/properties`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating property'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/admin/properties/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating property'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/admin/properties/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting property'); }
    }
  },

  // Contact / Leads
  contact: {
    submit: async (contactData) => {
      try {
        const response = await axios.post(`${API_BASE}/contact`, contactData);
        return response.data;
      } catch (error) { return handleError(error, 'submitting contact form'); }
    }
  },
  contacts: {
    getAll: async () => {
      try {
        const response = await axios.get(`${API_BASE}/admin/contacts`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching contacts'); }
    },
    getById: async (id) => {
      try {
        const response = await axios.get(`${API_BASE}/admin/contacts/${id}`);
        return asObject(response.data);
      } catch (error) { return handleError(error, `fetching contact ${id}`); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/admin/contacts`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating contact'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/admin/contacts/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating contact'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/admin/contacts/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting contact'); }
    },
    updateStatus: async (id, status) => {
      try {
        const response = await axios.patch(`${API_BASE}/admin/contacts/${id}/status?status=${status}`);
        return response.data;
      } catch (error) { return handleError(error, 'updating contact status'); }
    }
  },

  // Reviews
  reviews: {
    getByProperty: async (propertyId) => {
      try {
        const response = await axios.get(`${API_BASE}/reviews?property_id=${propertyId}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching reviews'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/reviews`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating review'); }
    }
  },

  // Auth
  auth: {
    login: async (email, password) => {
      try {
        const response = await axios.post(`${API_BASE}/auth/login`, { email, password });
        return response.data;
      } catch (error) { return handleError(error, 'login'); }
    },
    me: async () => {
      try {
        const response = await axios.get(`${API_BASE}/auth/me`);
        return asObject(response.data);
      } catch (error) { return handleError(error, 'fetching current user'); }
    }
  },

  // Users
  users: {
    getAll: async () => {
      try {
        const response = await axios.get(`${API_BASE}/admin/users`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching users'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/admin/users`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating user'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/admin/users/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating user'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/admin/users/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting user'); }
    }
  },

  // CMS Settings
  settings: {
    get: async () => {
      try {
        const response = await axios.get(`${API_BASE}/cms/settings`);
        return asObject(response.data);
      } catch (error) { return handleError(error, 'fetching settings'); }
    },
    update: async (data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/settings`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating settings'); }
    }
  },

  // Banners
  banners: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/banners${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching banners'); }
    },
    getByKey: async (key) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/banners/${key}`);
        return asObject(response.data);
      } catch (error) { return handleError(error, `fetching banner ${key}`); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/banners`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating banner'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/banners/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating banner'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/banners/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting banner'); }
    }
  },

  // Agents
  agents: {
    getAll: async () => {
      try {
        const response = await axios.get(`${API_BASE}/cms/agents`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching agents'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/agents`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating agent'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/agents/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating agent'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/agents/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting agent'); }
    }
  },

  // Visits
  visits: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/admin/visits${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching visits'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/admin/visits`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating visit'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/admin/visits/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating visit'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/admin/visits/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting visit'); }
    }
  },

  // Leads
  leads: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/leads${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching leads'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/leads`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating lead'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/leads/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating lead'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/leads/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting lead'); }
    }
  },

  // Transactions
  transactions: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/transactions${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching transactions'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/transactions`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating transaction'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/transactions/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating transaction'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/transactions/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting transaction'); }
    }
  },

  // Payments
  payments: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/payments${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching payments'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/payments`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating payment'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/payments/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating payment'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/payments/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting payment'); }
    }
  },

  // Contracts
  contracts: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/contracts${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching contracts'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/contracts`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating contract'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/contracts/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating contract'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/contracts/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting contract'); }
    }
  },

  // Documents
  documents: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/documents${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching documents'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/documents`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating document'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/documents/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating document'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/documents/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting document'); }
    }
  },

  // Calendar Events
  calendarEvents: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/calendar-events${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching calendar events'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/calendar-events`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating calendar event'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/calendar-events/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating calendar event'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/calendar-events/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting calendar event'); }
    }
  },

  // Notifications
  notifications: {
    getAll: async (unreadOnly = false) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/notifications${unreadOnly ? '?unread_only=true' : ''}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching notifications'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/notifications`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating notification'); }
    },
    markRead: async (id) => {
      try {
        const response = await axios.patch(`${API_BASE}/cms/notifications/${id}`, { is_read: true });
        return response.data;
      } catch (error) { return handleError(error, 'marking notification read'); }
    }
  },

  // Map
  map: {
    getMarkers: async () => {
      try {
        const response = await axios.get(`${API_BASE}/cms/map-markers`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching map markers'); }
    }
  },

  // CMS Pages
  cmsPages: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/pages${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching CMS pages'); }
    },
    getBySlug: async (slug) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/pages/slug/${slug}`);
        return asObject(response.data);
      } catch (error) { return handleError(error, `fetching CMS page ${slug}`); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/pages`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating CMS page'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/pages/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating CMS page'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/pages/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting CMS page'); }
    }
  },

  // Media Library
  media: {
    getAll: async (params = {}) => {
      try {
        const response = await axios.get(`${API_BASE}/cms/media${buildQuery(params)}`);
        return asArray(response.data);
      } catch (error) { return handleError(error, 'fetching media'); }
    },
    create: async (data) => {
      try {
        const response = await axios.post(`${API_BASE}/cms/media`, data);
        return response.data;
      } catch (error) { return handleError(error, 'creating media'); }
    },
    update: async (id, data) => {
      try {
        const response = await axios.put(`${API_BASE}/cms/media/${id}`, data);
        return response.data;
      } catch (error) { return handleError(error, 'updating media'); }
    },
    delete: async (id) => {
      try {
        const response = await axios.delete(`${API_BASE}/cms/media/${id}`);
        return response.data;
      } catch (error) { return handleError(error, 'deleting media'); }
    }
  },

  // Admin
  admin: {
    stats: async () => {
      try {
        const response = await axios.get(`${API_BASE}/admin/stats`);
        return asObject(response.data);
      } catch (error) { return handleError(error, 'fetching admin stats'); }
    },
    overview: async () => {
      try {
        const response = await axios.get(`${API_BASE}/admin/dashboard/overview`);
        return asObject(response.data);
      } catch (error) { return handleError(error, 'fetching dashboard overview'); }
    },
    analytics: async () => {
      try {
        const response = await axios.get(`${API_BASE}/admin/analytics`);
        return asObject(response.data);
      } catch (error) { return handleError(error, 'fetching analytics'); }
    }
  },

  // Uploads
  uploads: {
    uploadImage: async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_BASE}/admin/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    deleteImage: async (filename) => {
      const response = await axios.delete(`${API_BASE}/admin/upload-image/${filename}`);
      return response.data;
    }
  }
};

export default api;
